import { useState, useCallback } from 'react'
import { message } from 'antd'
import {
  listOutfits,
  generateOutfit
} from '../../../services/api/aiVideoGeneration'
import { useTaskPoller } from './useTaskPoller'
import { usePresignedUpload } from './usePresignedUpload'

/**
 * Custom hook to manage wardrobe library API calls and polling
 */
export const useWardrobe = ({ videoId } = {}) => {
  const [outfits, setOutfits] = useState([])
  const [localLoading, setLocalLoading] = useState(false)
  const [isCreatingOutfit, setIsCreatingOutfit] = useState(false)
  const [error, setError] = useState(null)

  const {
    isPolling,
    progress,
    startPolling,
    stopPolling: stopTaskPolling
  } = useTaskPoller({ pollInterval: 5000 })

  const outfitCreationPoller = useTaskPoller({ pollInterval: 3000 })
  const { uploadFiles } = usePresignedUpload({ videoId })

  const fetchLibrary = useCallback(
    async (params = {}) => {
      setLocalLoading(true)
      setError(null)

      try {
        const res = await listOutfits({
          action: params.action || 'list',
          page: params.page || 1,
          page_size: params.pageSize || 20,
          video_id: videoId,
          ...params
        })

        let resultData = null
        let paginationData = null

        // Check if it returned a taskId for polling or immediate data
        if (res.data?.task_id) {
          const taskResult = await startPolling(res.data.task_id)
          if (taskResult && taskResult.status === 'success') {
            resultData = taskResult.output || taskResult
            // Pagination can be at taskResult.pagination OR inside output.pagination
            paginationData =
              taskResult.pagination || resultData?.pagination || null
          }
        } else {
          // Handle immediate success cases
          resultData = res.data?.output || res.data
          // Pagination can be at root or inside output
          paginationData =
            res.data?.pagination || resultData?.pagination || null
        }

        if (
          params.action === 'list' ||
          params.action === 'search' ||
          params.action === 'filter' ||
          !params.action
        ) {
          // Handle different response structures:
          // - resultData.items (when output is an object with items array)
          // - resultData itself (when output is directly the array)
          const items = Array.isArray(resultData)
            ? resultData
            : resultData?.items || []
          setOutfits(items)
          // Return both items and pagination
          return { items, pagination: paginationData }
        }

        return resultData
      } catch (err) {
        setError(err.message)
        console.error(`Wardrobe ${params.action || 'fetch'} error:`, err)
        message.error(
          err.message || `Failed to perform ${params.action || 'fetch'} action`
        )
        throw err
      } finally {
        setLocalLoading(false)
      }
    },
    [startPolling, videoId]
  )

  /**
   * Create a new outfit using AI generation
   * @param {Object} params - Creation parameters
   * @param {string} params.prompt - Description of the outfit to generate
   * @param {File} [params.referenceImage] - Optional reference image file
   * @returns {Promise<Object>} - The generated outfit data
   */
  const createOutfit = useCallback(
    async ({ prompt, referenceImages = [] }) => {
      if (!prompt || !prompt.trim()) {
        message.error('Please enter a description for the outfit')
        return { success: false, error: new Error('Prompt is required') }
      }

      setIsCreatingOutfit(true)
      setError(null)

      try {
        let referenceImageUrls = []

        // Upload reference images if provided
        if (referenceImages && referenceImages.length > 0) {
          const uploadResult = await uploadFiles({
            files: referenceImages,
            category: 'image',
            folder: 'outfit-references'
          })

          if (uploadResult.success && uploadResult.urls.length > 0) {
            referenceImageUrls = uploadResult.urls
          }
        }

        // Call generateOutfit API
        const response = await generateOutfit({
          prompt: prompt.trim(),
          reference_images: referenceImageUrls,
          video_id: videoId
        })

        const data = response.data

        // Check for task_id and poll
        const taskId =
          data.task_id ||
          data.output?.task_id ||
          (data.status === 'pending' && data.id)

        let outfitData = null

        if (taskId) {
          const pollResult = await outfitCreationPoller.startPolling(taskId)
          outfitData = pollResult.output || pollResult
        } else if (data.status === 'success' && data.output) {
          outfitData = data.output
        } else {
          throw new Error('Failed to initiate outfit generation')
        }

        if (outfitData) {
          // Refresh library to include the new outfit
          fetchLibrary({ page: 1 })

          message.success('Outfit created successfully!')
          return { success: true, data: outfitData }
        }

        throw new Error('No outfit data returned')
      } catch (err) {
        setError(err.message)
        console.error('Create outfit error:', err)
        message.error(err.message || 'Failed to create outfit')
        return { success: false, error: err }
      } finally {
        setIsCreatingOutfit(false)
      }
    },
    [uploadFiles, outfitCreationPoller, fetchLibrary, videoId]
  )

  const getOutfit = useCallback(
    async tag => {
      return fetchLibrary({ action: 'get', tag })
    },
    [fetchLibrary]
  )

  const searchOutfits = useCallback(
    async query => {
      return fetchLibrary({ action: 'search', query })
    },
    [fetchLibrary]
  )

  const filterOutfits = useCallback(
    async filters => {
      return fetchLibrary({ action: 'filter', ...filters })
    },
    [fetchLibrary]
  )

  const getStats = useCallback(async () => {
    return fetchLibrary({ action: 'stats' })
  }, [fetchLibrary])

  const checkExists = useCallback(
    async tag => {
      return fetchLibrary({ action: 'exists', tag })
    },
    [fetchLibrary]
  )

  const deleteOutfit = useCallback(
    async tag => {
      try {
        const res = await fetchLibrary({ action: 'delete', tag })
        message.success(`Outfit ${tag} deleted successfully`)
        // Refresh library after deletion if on library tab
        fetchLibrary()
        return res
      } catch (err) {
        // Error already handled in fetchLibrary
        throw err
      }
    },
    [fetchLibrary]
  )

  const stopPolling = useCallback(() => {
    stopTaskPolling()
    setLocalLoading(false)
  }, [stopTaskPolling])

  return {
    outfits,
    // Use localLoading for the initial fetch and wrap the polling duration
    // isPolling is only true during actual background polling
    isLoading: localLoading || isPolling,
    isCreatingOutfit,
    creationProgress: outfitCreationPoller.progress,
    progress,
    error,
    fetchLibrary,
    createOutfit,
    getOutfit,
    searchOutfits,
    filterOutfits,
    getStats,
    checkExists,
    deleteOutfit,
    stopPolling
  }
}

export default useWardrobe
